import { Box, IconButton, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../../../containers/ApiContainer';
import { useSnackbar } from '../../../../containers/SnackbarContainer';
import { useHoldingArea } from '../../../../hooks/useHoldingArea';
import { TenantService } from '../../../../services/TenantService';
import { Tenant, Trip } from '../../../../types';
import { AddTenantButton } from '../../../Settings/HangarSettings/AddTenantButton';
import { InboxFilters, TimeFilter } from '../../../OperationsInbox/types';

type Props = {
  filters: InboxFilters;
  onFiltersChange: (filters: InboxFilters) => void;
  totalCount: number;
  onRefresh: () => void;
};

/**
 * QueueHeader - filter controls and refresh for the ops queue
 */
export const QueueHeader: React.FC<Props> = ({
  filters,
  onFiltersChange,
  totalCount,
  onRefresh,
}) => {
  const { t } = useTranslation();
  const { postgrest } = useApi();
  const snackbar = useSnackbar();
  const holdingAreaTracking = useHoldingArea();
  const tenantService = React.useMemo(() => new TenantService(postgrest), [postgrest]);

  const handleTimeFilterChange = (
    _event: React.MouseEvent<HTMLElement>,
    newFilter: TimeFilter | null
  ) => {
    if (newFilter !== null) {
      onFiltersChange({ ...filters, timeFilter: newFilter });
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({ ...filters, search: event.target.value });
  };

  // Handler for adding aircraft to the holding area
  const handleAddAircraft = React.useCallback(
    async (tenant: Tenant | Tenant[]) => {
      try {
        const tenantArray = Array.isArray(tenant) ? tenant : [tenant];

        for (const t of tenantArray) {
          // Create tenant in database
          await tenantService.createTenant(t);
          // Track in holding area
          await holdingAreaTracking.addTenant(t);
        }

        snackbar.notify({
          text: t('screens.ops.messages.aircraftAdded', 'Aircraft added successfully'),
          severity: 'success',
        });

        // Refresh the queue to show any new items
        onRefresh();
      } catch (err) {
        console.error('Failed to add aircraft:', err);
        snackbar.notify({
          text: t('screens.ops.messages.addAircraftError', 'Failed to add aircraft'),
          severity: 'error',
        });
      }
    },
    [tenantService, holdingAreaTracking, snackbar, t, onRefresh]
  );

  // Handler for adding trips - just refresh the queue to show the new unassigned trip
  const handleAddTrip = React.useCallback(
    (_trip: Trip) => {
      onRefresh();
    },
    [onRefresh]
  );

  return (
    <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
      {/* Row 1: Time filters + action icons */}
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <ToggleButtonGroup
          value={filters.timeFilter}
          exclusive
          onChange={handleTimeFilterChange}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              px: 1.5,
              py: 0.5,
              borderRadius: '16px !important',
              border: '1px solid',
              borderColor: 'divider',
              fontSize: '0.8rem',
              '&:not(:first-of-type)': {
                marginLeft: '6px',
                borderLeft: '1px solid',
                borderLeftColor: 'divider',
              },
              '&.Mui-selected': {
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
              },
            },
          }}
        >
          <ToggleButton value="today">Today</ToggleButton>
          <ToggleButton value="week">Week</ToggleButton>
          <ToggleButton value="all">All</ToggleButton>
        </ToggleButtonGroup>

        {/* Right-aligned icons */}
        <Box display="flex" alignItems="center" ml="auto" gap={0.5}>
          <IconButton size="small" onClick={onRefresh} title="Refresh">
            <RefreshIcon fontSize="small" />
          </IconButton>
          <AddTenantButton
            location={null}
            sendToLocation={() => {}}
            stack={null}
            setStack={() => {}}
            onAdd={handleAddAircraft}
            hideAddMovableObstacle
            bulk={false}
            iconButton
            isScheduledView
            onAddTrip={handleAddTrip}
            currentTime={new Date()}
          />
        </Box>
      </Box>

      {/* Row 2: Search input */}
      <TextField
        size="small"
        placeholder="Search..."
        value={filters.search}
        onChange={handleSearchChange}
        fullWidth
        InputProps={{
          startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
          },
          '& .MuiInputBase-input': {
            py: 0.75,
            fontSize: '0.875rem',
          },
        }}
      />
    </Box>
  );
};
